'use client'

import { Badge, InitialsAvatar, cn, calendarDaysSinceIso } from '@yeshuman/sdk'
import type { CandidatePanelSource } from './candidate-panel-types'
import { resolveCandidateCommitment } from './candidate-panel-types'

export type CandidateHeaderProps = {
  source: CandidatePanelSource
  /** When set, shown instead of applicant-only location from source. */
  locationOverride?: string | null
  className?: string
}

function locationLine(source: CandidatePanelSource): string | null {
  if (source.kind === 'applicant') {
    const { city, country } = source.applicant.profile
    if (city && country) return `${city}, ${country}`
    return city ?? country ?? null
  }
  return null
}

/** Stage name lives on the action control — keep this line as duration / commitment only. */
function statusLine(source: CandidatePanelSource): string {
  const commitment = resolveCandidateCommitment(source)
  if (commitment === 'prospect') return 'Prospect · not in pipeline'
  if (source.kind !== 'applicant') return 'Applicant'
  const applicant = source.applicant
  if (commitment === 'invited') {
    const days = calendarDaysSinceIso(applicant.applied_at)
    return `Invited · awaiting application · ${days}d`
  }
  if (typeof applicant.days_in_stage === 'number') {
    return `${applicant.days_in_stage}d in stage`
  }
  return 'In pipeline'
}

export function CandidateHeader({ source, locationOverride, className }: CandidateHeaderProps) {
  const commitment = resolveCandidateCommitment(source)
  const profile =
    source.kind === 'applicant'
      ? source.applicant.profile
      : {
          id: source.match.profile_id,
          name: source.match.name,
          headline: source.match.headline,
          avatar_url: source.match.avatar_url,
        }
  const avatarSeed = profile.id

  const rank = source.kind === 'applicant' ? source.rank : undefined
  const location = locationOverride ?? locationLine(source)
  const status = statusLine(source)
  const invitedTone = commitment === 'invited'

  return (
    <section
      className={cn(
        'flex items-start gap-4 rounded-lg border p-4',
        commitment === 'applied' ? 'border-border/80' : 'border-dashed border-border/80',
        className,
      )}
    >
      <InitialsAvatar
        src={profile.avatar_url}
        colorSeed={avatarSeed}
        className="h-14 w-14 shrink-0 border border-border/80"
        alt={profile.name}
      />
      <div className="min-w-0 flex-1 space-y-1">
        <div className="flex flex-wrap items-center gap-2">
          {typeof rank === 'number' ? (
            <span className="font-mono text-sm text-muted-foreground tabular-nums">#{rank}</span>
          ) : null}
          {profile.headline ? (
            <p className="min-w-0 text-sm font-medium text-foreground">{profile.headline}</p>
          ) : null}
        </div>
        {location ? <p className="text-sm text-muted-foreground">{location}</p> : null}
        <p
          className={cn(
            'text-xs',
            invitedTone ? 'text-warning' : 'text-muted-foreground',
          )}
        >
          {status}
        </p>
        {source.kind === 'applicant' && source.applicant.source ? (
          <Badge variant="secondary" className="text-xs uppercase tracking-wide">
            {source.applicant.source}
          </Badge>
        ) : null}
      </div>
    </section>
  )
}
